"use strict";

// Some code for dealing with .arkio files

/**
 * Reads a variable-length unsigned 32-bit integer from a Uint8Array.
 * @param {Uint8Array} buffer The buffer to read from.
 * @param {number} offset The starting offset in the buffer.
 * @returns {{value: number, newOffset: number}} The decoded value and the new offset after reading.
 */
const readVarUInt32 = function(buffer, offset) {
    let value = 0;
    let shift = 0;
    let currentOffset = offset;
    let byte;

    do {
        if (currentOffset >= buffer.length) {
            throw new Error("Buffer underflow while reading VarInt");
        }
        byte = buffer[currentOffset++];
        value |= (byte & 0x7f) << shift;
        shift += 7;
    } while (byte >= 128);

    return { value, newOffset: currentOffset };
};

/**
 * Writes a variable-length unsigned 32-bit integer.
 * @param {number} value The integer to write.
 * @returns {Uint8Array} A buffer containing the encoded integer.
 */
const writeVarUInt32 = function(value) {
    const bytes = [];
    while (value >= 0x80) {
        bytes.push((value & 0x7f) | 0x80);
        value >>>= 7;
    }
    bytes.push(value & 0x7f);
    return new Uint8Array(bytes);
};

/**
 * Extracts the JSON metadata preamble from the bytes of an .arkio file.
 * @param {Uint8Array} fileBytes The raw bytes of the .arkio file.
 * @returns {string} The metadata preamble as a JSON string.
 */
const getArkioPreamble = function(fileBytes) {
    // 1. Skip header (Magic Number + Version, 8 bytes total)
    const preambleLengthOffset = 8;

    // 2. Read preamble length (VarInt)
    const { value: preambleLength, newOffset: offsetAfterVarInt } = readVarUInt32(fileBytes, preambleLengthOffset);

    // 3. Read preamble
    const preambleBytes = fileBytes.slice(offsetAfterVarInt, offsetAfterVarInt + preambleLength);
    const decoder = new TextDecoder('utf-8');
    return decoder.decode(preambleBytes);
};

/**
 * Updates the JSON metadata in an .arkio file's bytes.
 * @param {Uint8Array} originalFileBytes The raw bytes of the original .arkio file.
 * @param {string} newJsonPreamble The new metadata to write, as a JSON string.
 * @returns {Blob} A Blob containing the bytes of the new, updated .arkio file.
 */
const updateArkioPreamble = function(originalFileBytes, newJsonPreamble) {
    // 1. Get the header from the original file (first 8 bytes)
    const fileHeader = originalFileBytes.slice(0, 8);

    // 2. Find where the original preamble ends to get the rest of the file
    const { value: oldPreambleLength, newOffset: offsetAfterOldVarInt } = readVarUInt32(originalFileBytes, 8);
    const restOfFile = originalFileBytes.slice(offsetAfterOldVarInt + oldPreambleLength);

    // 3. Serialize the new JSON preamble into a compact string and encode it
    const parsedJson = JSON.parse(newJsonPreamble);
    const compactJsonString = JSON.stringify(parsedJson);
    const encoder = new TextEncoder();
    const newPreambleBytes = encoder.encode(compactJsonString);

    // 4. Encode the new preamble length as a VarInt
    const newPreambleLengthBytes = writeVarUInt32(newPreambleBytes.length);

    // 5. Assemble the new file from its parts and return as a Blob
    const newFileParts = [
        fileHeader,
        newPreambleLengthBytes,
        newPreambleBytes,
        restOfFile
    ];
    
    return new Blob(newFileParts, { type: 'application/octet-stream' });
};

module.exports = {
    readVarUInt32,
    writeVarUInt32,
    getArkioPreamble,
    updateArkioPreamble
};
